import type { CivilDate } from '../../shared/date/civil.js'
import { validPoints } from './insights.js'
import type { PricePoint, Product } from './product.js'
import { baseUnitOf } from './unit.js'
import type { BaseUnit, MilliCents } from './unit.js'

/**
 * A **família de produto**: "Coca-Cola", que existe em 350 ml, 600 ml, 2 L e
 * fardo de 6.
 *
 * ## Por que a embalagem não podia continuar sendo a unidade de comparação
 *
 * `Product` é a **embalagem**: ela tem código de barras, é o que a linha da nota
 * casa, e é dela que sai o ponto de preço. Isso está certo e não muda.
 *
 * O que estava faltando é que *"o que vai compensar é o custo por litro"* — e
 * essa pergunta atravessa as embalagens. Com cada tamanho sendo um produto
 * isolado, o app respondia "o menor preço da Coca 2 L" e "o menor preço da Coca
 * 350 ml" separadamente, que é justamente a comparação que ninguém precisa
 * fazer: ninguém escolhe entre duas garrafas de 2 L, escolhe entre a de 2 L e a
 * lata.
 *
 * ## Família é uma ENTIDADE com id, e não um campo de texto
 *
 * Um `familyName: string` seria muito mais barato — e reproduziria, um nível
 * acima, exatamente o erro que `ProductMatch.md` chama de *"o erro clássico que
 * arruína catálogos"*: "Coca-Cola", "Coca Cola" e "COCA-COLA" viram três
 * famílias, e a comparação para de significar. Com id, "a mesma família" é uma
 * afirmação verificável.
 *
 * ## Um nível só, e sem árvore
 *
 * Não há família de família. "Coca-Cola Zero" é **outra** família, e não uma
 * sub-família de "Coca-Cola": quem compara refrigerante normal com zero está
 * escolhendo outra coisa, não outro tamanho. Uma árvore aqui traria `path`,
 * `depth`, ciclos e uma Cloud Function para manter tudo isso — o preço que
 * `Category` paga porque orçamento realmente é hierárquico. Tamanho não é.
 *
 * ## A comparação recusa o que não compara, e DIZ que recusou
 *
 * `ml` e `g` não se comparam sem densidade, que este app não tem (ver
 * `unit.ts`). Uma família com variações em bases diferentes não vira uma
 * comparação silenciosa: as incomparáveis saem da ordenação **e aparecem
 * nomeadas**. Esconder seria pior — a pessoa acharia que viu a lista inteira.
 */

export type ProductFamily = {
  id: string
  householdId: string
  /** "Coca-Cola". Sem tamanho no nome — o tamanho é da embalagem. */
  name: string
  normalizedName: string
  archivedAt: string | null
  createdAt: string
  updatedAt: string
}

export type VariantPrice = {
  product: Product
  baseUnit: BaseUnit
  /** O último pago, por unidade base. `null` quando nunca se comprou. */
  lastMilliCents: MilliCents | null
  lastAt: CivilDate | null
  /** O menor já visto — pago ou observado. */
  minMilliCents: MilliCents | null
  /**
   * O número pelo qual esta variação foi ordenada: o último pago, ou o menor
   * visto quando nunca se comprou. A tela mostra **qual dos dois** foi usado, e
   * é por isso que este campo anda junto de `usouUltimo`.
   */
  referenceMilliCents: MilliCents
  usouUltimo: boolean
}

export type FamilyComparison = {
  /** A base em que a família é comparada. `null` quando não há o que comparar. */
  baseUnit: BaseUnit | null
  /** Da mais barata para a mais cara, por unidade base. */
  ranked: VariantPrice[]
  /** Variações em outra base. **Nomeadas, e não escondidas.** */
  incomparable: Product[]
  /** Variações sem preço nenhum, e sem tamanho declarado. Idem. */
  semPreco: Product[]
}

/**
 * A comparação de uma família.
 *
 * `pointsByProduct` é o histórico de cada variação — a mesma lista que a tela de
 * produto já carrega. **Nada aqui olha `Product.stats`**, e é deliberado: o
 * resumo não guarda "último preço por unidade base", só o total da última
 * compra. Derivar dos pontos evita um campo novo, uma migração, e produtos com
 * resumo velho respondendo errado até alguém comprar de novo.
 */
export function compareFamily(
  variants: Product[],
  pointsByProduct: Map<string, PricePoint[]>,
): FamilyComparison {
  const vivos = variants.filter((p) => p.archivedAt === null)
  const comBase = vivos.filter((p) => p.unit !== null)
  const semBase = vivos.filter((p) => p.unit === null)

  const base = baseDominante(comBase)
  if (base === null) {
    return { baseUnit: null, ranked: [], incomparable: [], semPreco: vivos }
  }

  const naBase = comBase.filter((p) => p.unit !== null && baseUnitOf(p.unit) === base)
  const incomparable = comBase.filter((p) => p.unit !== null && baseUnitOf(p.unit) !== base)

  const precificadas: VariantPrice[] = []
  const semPreco: Product[] = [...semBase]

  for (const product of naBase) {
    const preco = precoDe(product, base, pointsByProduct.get(product.id) ?? [])
    if (preco === null) semPreco.push(product)
    else precificadas.push(preco)
  }

  return {
    baseUnit: base,
    // Menor primeiro; o id desempata para a ordem não mudar entre leituras.
    ranked: precificadas.sort(
      (a, b) =>
        a.referenceMilliCents - b.referenceMilliCents || a.product.id.localeCompare(b.product.id),
    ),
    incomparable,
    semPreco,
  }
}

/**
 * A base em que a família é comparada: a de **mais variações**.
 *
 * Empate desempata pelo nome da base, para a resposta não depender da ordem em
 * que os produtos chegaram — uma comparação que muda de base a cada releitura
 * seria pior do que nenhuma.
 */
function baseDominante(variants: Product[]): BaseUnit | null {
  const contagem = new Map<BaseUnit, number>()
  for (const p of variants) {
    if (p.unit === null) continue
    const base = baseUnitOf(p.unit)
    contagem.set(base, (contagem.get(base) ?? 0) + 1)
  }
  const ordenadas = [...contagem.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  return ordenadas[0]?.[0] ?? null
}

/** `null` quando a variação não tem preço nenhum — nem pago, nem visto. */
function precoDe(
  product: Product,
  base: BaseUnit,
  points: PricePoint[],
): VariantPrice | null {
  // Ponto corrigido não entra em conta nenhuma — mesma exclusão de `insights`.
  const validos = validPoints(points).filter((p) => p.baseUnit === base)
  if (validos.length === 0) return null

  const compras = [...validos]
    .filter((p) => p.source === 'purchase')
    .sort((a, b) => a.observedAt.localeCompare(b.observedAt) || a.createdAt.localeCompare(b.createdAt))
  const ultima = compras[compras.length - 1] ?? null

  const menor = [...validos].sort(
    (a, b) => a.pricePerBaseUnitMilliCents - b.pricePerBaseUnitMilliCents,
  )[0]
  const minMilliCents = menor?.pricePerBaseUnitMilliCents ?? null

  // O último pago é o que responde "quanto isso me custa hoje". Sem compra
  // nenhuma, o menor visto é o que se sabe — e a tela diz qual dos dois usou.
  const referencia = ultima?.pricePerBaseUnitMilliCents ?? minMilliCents
  if (referencia === null) return null

  return {
    product,
    baseUnit: base,
    lastMilliCents: ultima?.pricePerBaseUnitMilliCents ?? null,
    lastAt: ultima?.observedAt ?? null,
    minMilliCents,
    referenceMilliCents: referencia,
    usouUltimo: ultima !== null,
  }
}

/** Quanto a mais a variação mais cara custa, em fração. `null` sem par. */
export function penalidade(comparison: FamilyComparison): number | null {
  const barata = comparison.ranked[0]
  const cara = comparison.ranked[comparison.ranked.length - 1]
  if (barata === undefined || cara === undefined || barata === cara) return null
  if (barata.referenceMilliCents === 0) return null
  return (cara.referenceMilliCents - barata.referenceMilliCents) / barata.referenceMilliCents
}
